import React, { useState } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../ui/Table';
import Button from '../../ui/Button';
import { Copy, Trash2 } from 'lucide-react';

const DetailItem = ({ label, value }) => (
  <div className="flex flex-col gap-1 mb-4">
    <span className="font-semibold text-gray-900 text-[12px] uppercase tracking-wider">{label}</span>
    <span className="text-gray-600 text-[13px]">{value || '-'}</span>
  </div>
);

const ClientTableRow = ({ client, loadClientToForm, handleDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <Tr 
        className={`group cursor-pointer hover:bg-gray-50/50 transition-colors ${isExpanded ? 'bg-gray-50/50' : 'border-b border-gray-50 last:border-0'}`} 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Td className="font-medium text-gray-900">{client.name}</Td>
        <Td>{client.idNumber || client.vatNumber}</Td>
        <Td>{client.city}</Td>
        <Td className="max-w-[200px] truncate">{client.address}</Td>
        <Td>{client.phoneNumber}</Td>
        <Td onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
            <button type="button" onClick={() => loadClientToForm(client)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-tenant-hover rounded-md transition-colors bg-white" title="Редактирай">
              <Copy size={16} strokeWidth={2} />
            </button>
            <button type="button" onClick={() => handleDelete(client.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-md transition-colors bg-white" title="Изтрий">
              <Trash2 size={16} strokeWidth={2} />
            </button>
          </div>
        </Td>
      </Tr>
      
      {isExpanded && (
        <Tr className="border-b border-gray-100 bg-gray-50/30">
          <Td colSpan={6} className="p-0">
            <div className="p-6 overflow-hidden">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
                <DetailItem label="Име на фирма" value={client.name} />
                <DetailItem label="ЕИК" value={client.idNumber} />
                <DetailItem label="ДДС Номер" value={client.vatNumber} />
                <DetailItem label="МОЛ" value={client.mol} />
                <DetailItem label="Град" value={client.city} />
                <DetailItem label="Адрес" value={client.address} />
                <DetailItem label="Телефон" value={client.phoneNumber} />
              </div>
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

const ClientsListTable = ({
  clients = [],
  handleUpdateClient,
  handleDelete,
  loadMore,
  sorting,
  setSorting,
  loadClientToForm
}) => {
  const isSorted = (key, direction) => sorting && sorting.key === key && sorting.direction === direction;

  return (
    <div className="px-6 pb-6">
      <div className="border border-gray-100 rounded-xl overflow-hidden mb-4">
        <Table>
          <Thead className="bg-white">
            <Tr className="border-b border-gray-100 hover:bg-transparent">
              <Th>
                <div className="flex items-center gap-2">
                  Име
                  <div className="flex flex-col cursor-pointer">
                    <span onClick={() => setSorting({ key: 'name', direction: 'asc' })} className={`text-[10px] -mb-1 ${isSorted('name', 'asc') ? 'text-tenant-hover' : 'text-gray-300 hover:text-gray-500'}`}>▲</span>
                    <span onClick={() => setSorting({ key: 'name', direction: 'desc' })} className={`text-[10px] ${isSorted('name', 'desc') ? 'text-tenant-hover' : 'text-gray-300 hover:text-gray-500'}`}>▼</span>
                  </div>
                </div>
              </Th>
              <Th>
                <div className="flex items-center gap-2">
                  ЕИК
                  <div className="flex flex-col cursor-pointer">
                    <span onClick={() => setSorting({ key: 'idNumber', direction: 'asc' })} className={`text-[10px] -mb-1 ${isSorted('idNumber', 'asc') ? 'text-tenant-hover' : 'text-gray-300 hover:text-gray-500'}`}>▲</span>
                    <span onClick={() => setSorting({ key: 'idNumber', direction: 'desc' })} className={`text-[10px] ${isSorted('idNumber', 'desc') ? 'text-tenant-hover' : 'text-gray-300 hover:text-gray-500'}`}>▼</span>
                  </div>
                </div>
              </Th>
              <Th>
                <div className="flex items-center gap-2">
                  Град
                  <div className="flex flex-col cursor-pointer">
                    <span onClick={() => setSorting({ key: 'city', direction: 'asc' })} className={`text-[10px] -mb-1 ${isSorted('city', 'asc') ? 'text-tenant-hover' : 'text-gray-300 hover:text-gray-500'}`}>▲</span>
                    <span onClick={() => setSorting({ key: 'city', direction: 'desc' })} className={`text-[10px] ${isSorted('city', 'desc') ? 'text-tenant-hover' : 'text-gray-300 hover:text-gray-500'}`}>▼</span>
                  </div>
                </div>
              </Th>
              <Th>
                <div className="flex items-center gap-2">
                  Адрес
                </div>
              </Th>
              <Th>
                <div className="flex items-center gap-2">
                  Телефон
                </div>
              </Th>
              <Th className="text-gray-900 text-[13px] capitalize text-right">Действия</Th>
            </Tr>
          </Thead>
          <Tbody>
            {clients.length === 0 ? (
              <Tr>
                <Td colSpan={6} className="text-center text-gray-500 py-8">Няма намерени клиенти</Td>
              </Tr>
            ) : (
              clients.map((client) => (
                <ClientTableRow 
                  key={client.id} 
                  client={client} 
                  loadClientToForm={loadClientToForm} 
                  handleDelete={handleDelete} 
                />
              ))
            )}
          </Tbody>
        </Table>
      </div>
      
      {loadMore && (
        <div className="px-4 py-6 flex justify-center border-t border-gray-100">
          <Button type="button" onClick={loadMore} variant="primary" className="text-[13px] font-medium w-full sm:w-auto px-8 shadow-sm">
            Зареди още
          </Button>
        </div>
      )}
    </div>
  );
};

export default ClientsListTable;
